import { getErrorMessage } from '@monetize/utils/core';
import { useEffect } from 'react';
import { Link as ReactDomLink, useNavigate } from 'react-router-dom';
import { useResendVerificationLink } from '../../../api/usersService';
import AppLoading from '../../../components/App/AppLoading';
import {
  MBox,
  MButton,
  MHeading,
  MLink,
  MPageContainer,
  MText,
} from '../../../components/Monetize';
import { ROUTES } from '../../../constants';
import { useAuth } from '../../../services/auth0';
import Sentry from '../../../services/sentry';
import { useToast } from '../../../services/toast';

const VerifyEmail = () => {
  const { getAndHandleAccessTokenSilently } = useAuth();
  const { user, getDecodedToken } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const run = async () => {
      await getAndHandleAccessTokenSilently({ forceRefresh: true });
      if (user && user.email_verified) {
        Sentry.logger.info('VerifyEmail - email already verified, redirecting');
        navigate(ROUTES.ROOT);
      }
    };

    run();
  }, [user]);

  const { isPending: isLoading, mutate } = useResendVerificationLink({
    onSuccess: () => {
      addToast({
        summary: 'Email Sent',
        detail: `Verification email has been sent to ${user.email}.`,
        severity: 'info',
      });
    },
    onError: (error) => {
      Sentry.logger.error('VerifyEmail - resend verification failed', {
        error: getErrorMessage(error),
      });
      addToast({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to send verification email. Please try again later.',
      });
    },
  });

  const resendVerificationLink = async () => {
    Sentry.logger.info('VerifyEmail - resend verification requested');
    const tokenDecoded = getDecodedToken();
    tokenDecoded && mutate({ subject: tokenDecoded.sub });
  };

  if (!user) {
    return <AppLoading />;
  }
  if (user && user.email_verified) {
    return null;
  }

  return (
    <MPageContainer alignItems="center" justifyContent="center">
      <MHeading fontSize="xl" my="2">
        Verify Your Email Address
      </MHeading>
      <MText fontSize="sm" color="tGray.darkPurple">
        A verification link has been sent to <b>{user.email}</b>
      </MText>
      <MButton
        variant="primary"
        my="2"
        mt="4"
        onClick={resendVerificationLink}
        isLoading={isLoading}
      >
        Resend Verification Link
      </MButton>

      <MBox my="2" display="flex" alignItems="center">
        <MText mr="2">Not the right user?</MText>
        <MLink fontSize="sm" textDecor="underline" asChild>
          <ReactDomLink to={ROUTES.AUTH_LOGOUT}>Log out</ReactDomLink>
        </MLink>
      </MBox>
    </MPageContainer>
  );
};

export default VerifyEmail;
